// services/apiService.js

import axios from 'axios';
import { API_ENDPOINTS } from './config';
import { BlockBlobClient } from "@azure/storage-blob";
import pLimit from "p-limit";

function requireTabularIdentity(entraId) {
  if (typeof entraId !== 'string' || !entraId.trim()) {
    throw new Error('A signed-in account is required to access tabular datasets.');
  }
  return entraId;
}

export default {
  async get(endpoint, params = {}, entraId) {
    try {
      const fullParams = { ...params, entra_id: entraId };
      const response = await axios.get(endpoint, { params: fullParams });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch data from ${endpoint}:`, error);
      throw error;
    }
  },

  async createWopiSession(fileId, entraId, canWrite = true) {
    const payload = {
      file_id: Number(fileId),
      entra_id: entraId,
      can_write: Boolean(canWrite),
    };

    const candidateEndpoints = [
      `${process.env.VUE_APP_BASE_URL || '/api'}/wopi/sessions`,
      `${process.env.VUE_APP_BASE_URL || '/api'}/wopi/session`,
      `${process.env.VUE_APP_BASE_URL || '/api'}/api/wopi/sessions`,
      `${process.env.VUE_APP_BASE_URL || '/api'}/api/wopi/session`,
    ];

    let lastError = null;

    for (const endpoint of candidateEndpoints) {
      try {
        const response = await axios.post(endpoint, payload, {
          headers: { 'Content-Type': 'application/json' },
        });
        return response.data;
      } catch (error) {
        lastError = error;
        const status = error?.response?.status;
        if (status !== 404 && status !== 405) {
          throw error;
        }
      }
    }

    if (lastError) {
      throw lastError;
    }

    throw new Error('Unable to create WOPI session');
  },

  async fetchResources(endpoint, entraId) {
    try {
      const response = await axios.get(endpoint, { params: { entra_id: entraId } });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch resources from ${endpoint}:`, error);
      throw error;
    }
  },

  async getEntityDisplayImages(entityType, entityId, entraId) {
    try {
      const response = await axios.get(API_ENDPOINTS.entityDisplayImages(entityType, entityId), {
        params: { entra_id: entraId },
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch display images for ${entityType} ${entityId}:`, error);
      throw error;
    }
  },

  async markDisplayImage(fileId, entraId, displayOrder = null) {
    const payload = {};
    if (displayOrder != null) {
      payload.display_order = Number(displayOrder);
    }

    try {
      const response = await axios.post(API_ENDPOINTS.displayImage(fileId), payload, {
        params: { entra_id: entraId },
        headers: { 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to mark file ${fileId} as display image:`, error);
      throw error;
    }
  },

  async unmarkDisplayImage(fileId, entraId) {
    try {
      const response = await axios.delete(API_ENDPOINTS.displayImage(fileId), {
        params: { entra_id: entraId },
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to unmark file ${fileId} as display image:`, error);
      throw error;
    }
  },

  async updateDisplayImageOrder(fileId, displayOrder, entraId) {
    try {
      const response = await axios.put(
        API_ENDPOINTS.displayImageOrder(fileId),
        { display_order: Number(displayOrder) },
        {
          params: { entra_id: entraId },
          headers: { 'Content-Type': 'application/json' },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to update display image order for file ${fileId}:`, error);
      throw error;
    }
  },

  async reorderEntityDisplayImages(entityType, entityId, fileIds, entraId) {
    try {
      const response = await axios.put(
        API_ENDPOINTS.entityDisplayImages(entityType, entityId),
        { file_ids: fileIds.map((fileId) => Number(fileId)) },
        {
          params: { entra_id: entraId },
          headers: { 'Content-Type': 'application/json' },
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Failed to reorder display images for ${entityType} ${entityId}:`, error);
      throw error;
    }
  },

  async getProjectHierarchy(projectId, entraId, structureSetId = null) {
    const params = {};
    if (structureSetId != null) {
      params.structure_set_id = structureSetId;
    }
    return this.get(API_ENDPOINTS.projectHierarchy(projectId), params, entraId);
  },

  async getProjectStructureElements(projectId, entraId, parentElementId, structureSetId = null) {
    const params = {};
    if (parentElementId === null) {
      params.parent_element_id = 'null';
    } else if (parentElementId !== undefined) {
      params.parent_element_id = parentElementId;
    }
    if (structureSetId != null) {
      params.structure_set_id = structureSetId;
    }
    return this.get(API_ENDPOINTS.projectStructureElements(projectId), params, entraId);
  },

  async getProjectStructureAllowedChildTypes(projectId, parentElementId, entraId, structureSetId = null) {
    const params = parentElementId == null ? {} : { parent_id: parentElementId };
    if (structureSetId != null) {
      params.structure_set_id = structureSetId;
    }
    return this.get(API_ENDPOINTS.projectStructureAllowedChildTypes(projectId), params, entraId);
  },

  async getProjectStructureAllowedChildTypesForElement(projectId, parentElementId, entraId, structureSetId = null) {
    const params = { parent_id: parentElementId };
    if (structureSetId != null) {
      params.structure_set_id = structureSetId;
    }
    return this.get(API_ENDPOINTS.projectStructureAllowedChildTypes(projectId), params, entraId);
  },

  async getProjectStructureExperimentTargets(projectId, entraId, structureSetId = null) {
    const params = {};
    if (structureSetId != null) {
      params.structure_set_id = structureSetId;
    }
    return this.get(API_ENDPOINTS.projectStructureExperimentTargets(projectId), params, entraId);
  },

  async getExperimentsFromStructureElement(elementId, entraId) {
    return this.get(API_ENDPOINTS.elementsExperiments(elementId), {}, entraId);
  },

  async createProjectStructureElement(projectId, data, entraId, structureSetId = null) {
    const payload = structureSetId != null
      ? { ...data, structure_set_id: structureSetId }
      : data;
    return this.createResource(API_ENDPOINTS.projectStructureElements(projectId), payload, entraId);
  },

  async moveProjectStructureElementChild(parentElementId, elementId, position, entraId, structureSetId = null) {
    const payload = {
      position: Number(position),
      parent_id: parentElementId == null ? null : Number(parentElementId),
    };
    if (structureSetId != null) {
      payload.structure_set_id = structureSetId;
    }

    return this.createResource(
      API_ENDPOINTS.projectStructureElementMove(elementId),
      payload,
      entraId
    );
  },

  async getProjectStructureElement(elementId, entraId, structureSetId = null) {
    const params = {};
    if (structureSetId != null) {
      params.structure_set_id = structureSetId;
    }
    return this.get(API_ENDPOINTS.projectStructureElementById(elementId), params, entraId);
  },

  async updateProjectStructureElement(elementId, data, entraId, structureSetId = null) {
    const payload = structureSetId != null
      ? { ...data, structure_set_id: structureSetId }
      : data;
    const response = await axios.patch(
      API_ENDPOINTS.projectStructureElement(elementId),
      { ...payload, entra_id: entraId },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data;
  },

  async deleteProjectStructureElement(elementId, entraId, structureSetId = null) {
    const requestData = { entra_id: entraId };
    if (structureSetId != null) {
      requestData.structure_set_id = structureSetId;
    }
    const response = await axios.delete(API_ENDPOINTS.projectStructureElement(elementId), {
      data: requestData,
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  },

  // Session-based upload: Initialize session and get upload URLs
  async initUploadSession(fileType, entityId, files, entraId, folderId = null) {
    const fileList = Array.from(files);
    const originalRelPath = (f) =>
      (f.webkitRelativePath && f.webkitRelativePath.length > 0)
        ? f.webkitRelativePath
        : f.name;

    const initPayload = {
      file_type: fileType,
      entity_id: entityId,
      entra_id: entraId,
      folder_id: folderId,
      files: fileList.map((f) => ({
        name: f.name,
        relative_path: originalRelPath(f),
        size: f.size,
      })),
    };

    const initUrl = fileType === 'structure_element'
      ? API_ENDPOINTS.projectStructureElementFilesInitUpload(entityId)
      : `${API_ENDPOINTS.files}/init-upload`;
    const response = await axios.post(initUrl, initPayload, {
      headers: { "Content-Type": "application/json" },
    });

    const data = response.data || {};
    const session_id = data.session_id ?? data.sessionId ?? data.upload_session_id ?? null;
    const upload_info = Array.isArray(data.upload_info)
      ? data.upload_info
      : Array.isArray(data.files)
        ? data.files
        : Array.isArray(data.uploadInfo)
          ? data.uploadInfo
          : [];

    return { ...data, session_id, upload_info }; // { session_id, upload_info: [...] }
  },

  // Complete individual file upload
  async completeFileUpload(sessionId, blobPath, fileType, entityId, entraId, folderId = null) {
    const completeUrl = fileType === 'structure_element'
      ? API_ENDPOINTS.projectStructureElementFilesCompleteUpload(entityId)
      : `${API_ENDPOINTS.files}/complete-upload`;

    const response = await axios.post(
      completeUrl,
      { 
        session_id: sessionId,
        file_type: fileType,
        entity_id: entityId,
        entra_id: entraId,
        folder_id: folderId,
        files: [{ blob_path: blobPath }]
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return response.data; // { files: [...], failed: [...] }
  },

  // Refresh expired SAS URLs for pending/failed files
  async refreshUploadUrls(sessionId, fileType = null, entityId = null) {
    const refreshUrl = fileType === 'structure_element' && entityId != null
      ? API_ENDPOINTS.projectStructureElementFilesRefreshUploadUrls(entityId)
      : `${API_ENDPOINTS.files}/refresh-upload-urls`;

    const response = await axios.post(
      refreshUrl,
      { session_id: sessionId },
      { headers: { "Content-Type": "application/json" } }
    );
    const data = response.data || {};
    const upload_info = Array.isArray(data.upload_info)
      ? data.upload_info
      : Array.isArray(data.files)
        ? data.files
        : Array.isArray(data.uploadInfo)
          ? data.uploadInfo
          : [];

    return { ...data, upload_info }; // { upload_info: [...] }
  },

  // Finalize session and cleanup uncommitted files
  async finalizeUploadSession(sessionId, entraId, fileType = null, entityId = null) {
    const finalizeUrl = fileType === 'structure_element' && entityId != null
      ? API_ENDPOINTS.projectStructureElementFilesFinalizeUpload(entityId)
      : `${API_ENDPOINTS.files}/finalize-upload`;

    const response = await axios.post(
      finalizeUrl,
      {
        session_id: sessionId,
        entra_id: entraId,
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return response.data; // { message: "...", committed: N, abandoned: M }
  },

  // Upload file directly to Azure Blob Storage using SAS URL
  async uploadFileToBlob(uploadUrl, file, onProgress = null) {
    const blobClient = new BlockBlobClient(uploadUrl);
    
    await blobClient.uploadData(file, {
      blockSize: 8 * 1024 * 1024,
      concurrency: 3,
      maxSingleShotSize: 256 * 1024 * 1024,
      onProgress: onProgress ? (ev) => onProgress(ev.loadedBytes, file.size) : undefined,
    });
  },

  // High-level upload with session management and per-file tracking
  async uploadFiles(fileType, entityId, files, entraId, folderId = null, opts = {}) {
    const onProgress = typeof opts.onProgress === "function" ? opts.onProgress : null;
    const onFileProgress = typeof opts.onFileProgress === "function" ? opts.onFileProgress : null;
    const fileConcurrency = opts.fileConcurrency ?? 6;
    const emitIntervalMs = opts.emitIntervalMs ?? 200;

    const fileList = Array.from(files);
    const originalRelPath = (f) =>
      (f.webkitRelativePath && f.webkitRelativePath.length > 0)
        ? f.webkitRelativePath
        : f.name;

    const totalBytes = fileList.reduce((sum, f) => sum + (f.size || 0), 0);

    // Step 1: Initialize session
    const { session_id, upload_info } = await this.initUploadSession(
      fileType,
      entityId,
      files,
      entraId,
      folderId
    );

    if (!upload_info || !upload_info.length) {
      throw new Error("Init-upload returned no upload info");
    }

    // Create file map and tracking
    const fileByOriginalPath = new Map(fileList.map((f) => [originalRelPath(f), f]));
    const fileStatusMap = new Map(); // blob_path -> { status, file, uploadInfo, error }

    upload_info.forEach((info) => {
      const file = fileByOriginalPath.get(info.original_relative_path || info.filename);
      fileStatusMap.set(info.blob_path, {
        status: "pending",
        file,
        uploadInfo: info,
        error: null,
      });
    });

    // Progress tracking
    let globalUploadedBytes = 0;
    const bytesPerFile = new Map();
    let lastEmit = 0;

    const emitGlobalProgress = (currentFileName = "") => {
      if (!onProgress || totalBytes <= 0) return;

      const now = Date.now();
      if (now - lastEmit < emitIntervalMs) return;
      lastEmit = now;

      const percent = Math.min(100, Math.floor((globalUploadedBytes / totalBytes) * 100));
      onProgress({ percent, uploadedBytes: globalUploadedBytes, totalBytes, currentFileName });
    };

    // Step 2: Upload files in parallel
    const limit = pLimit(fileConcurrency);

    const uploadAndCommitFile = async (info) => {
      const fileState = fileStatusMap.get(info.blob_path);
      if (!fileState || !fileState.file) {
        throw new Error(`Could not match local file for: ${info.blob_path}`);
      }

      try {
        // Update status: uploading
        fileState.status = "uploading";
        if (onFileProgress) {
          onFileProgress(info.blob_path, "uploading", 0);
        }

        bytesPerFile.set(info.blob_path, 0);

        // Upload to blob storage
        await this.uploadFileToBlob(info.upload_url, fileState.file, (loaded, total) => {
          const prev = bytesPerFile.get(info.blob_path) || 0;
          const delta = loaded - prev;

          if (delta > 0) {
            globalUploadedBytes += delta;
            bytesPerFile.set(info.blob_path, loaded);
            emitGlobalProgress(fileState.file.name);
          }

          if (onFileProgress && total > 0) {
            const percent = Math.min(100, Math.floor((loaded / total) * 100));
            onFileProgress(info.blob_path, "uploading", percent);
          }
        });

        // Ensure full file is counted
        const prev = bytesPerFile.get(info.blob_path) || 0;
        const fileSize = fileState.file.size || 0;
        if (fileSize > prev) {
          globalUploadedBytes += (fileSize - prev);
          bytesPerFile.set(info.blob_path, fileSize);
        }

        // Update status: uploaded
        fileState.status = "uploaded";
        if (onFileProgress) {
          onFileProgress(info.blob_path, "uploaded", 100);
        }

        // Step 3: Commit individual file
        await this.completeFileUpload(session_id, info.blob_path, fileType, entityId, entraId, folderId);

        // Update status: committed
        fileState.status = "committed";
        if (onFileProgress) {
          onFileProgress(info.blob_path, "committed", 100);
        }

      } catch (error) {
        fileState.status = "failed";
        fileState.error = error.message || String(error);
        if (onFileProgress) {
          onFileProgress(info.blob_path, "failed", 0, fileState.error);
        }
        throw error;
      }
    };

    const tasks = upload_info.map((info) => limit(() => uploadAndCommitFile(info)));
    const results = await Promise.allSettled(tasks);

    // Final progress emit
    if (onProgress && totalBytes > 0) {
      onProgress({ percent: 100, uploadedBytes: globalUploadedBytes, totalBytes, currentFileName: "" });
    }

    // Step 4: Finalize session
    await this.finalizeUploadSession(session_id, entraId);

    // Check for failures
    const failures = results.filter((r) => r.status === "rejected");
    if (failures.length > 0) {
      const failedFiles = Array.from(fileStatusMap.values())
        .filter((f) => f.status === "failed")
        .map((f) => f.uploadInfo.filename);
      throw new Error(`${failures.length} file(s) failed to upload: ${failedFiles.join(", ")}`);
    }

    return { session_id, uploaded: upload_info.length };
  },

  // Retry a failed file upload (with URL refresh if needed)
  async retryFileUpload(sessionId, blobPath, file, uploadUrl, expiresAt, fileType, entityId, entraId, folderId = null, onProgress = null) {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const needsRefresh = (expiry - now) < 60000; // Refresh if < 1 min remaining

    let currentUploadUrl = uploadUrl;

    if (needsRefresh) {
      const { upload_info } = await this.refreshUploadUrls(sessionId);
      const refreshedInfo = upload_info.find((info) => info.blob_path === blobPath);
      if (!refreshedInfo) {
        throw new Error(`Could not find refreshed URL for: ${blobPath}`);
      }
      currentUploadUrl = refreshedInfo.upload_url;
    }

    // Re-upload
    await this.uploadFileToBlob(currentUploadUrl, file, onProgress);

    // Commit
    await this.completeFileUpload(sessionId, blobPath, fileType, entityId, entraId, folderId);
  },

  async deleteFile(fileId, userId, entraId) {
    try {
      const response = await axios.delete(API_ENDPOINTS.file(fileId), {
        data: { user_id: userId, entra_id: entraId },
        headers: { 'Content-Type': 'application/json' },
      });
      console.log("File deleted successfully.", response.data);
      return response.data;
    } catch (error) {
      console.error("Failed to delete file:", error);
      throw error;
    }
  },

  async completeFiles(fileType, entityId, entraId, files, folderId = null) {
    try {
      const response = await axios.post(
        `${API_ENDPOINTS.files}/complete-upload`,
        {
          file_type: fileType,
          entity_id: entityId,
          entra_id: entraId,
          folder_id: folderId,
          files,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to complete uploaded files:", error);
      throw error;
    }
  },

  async listTabularSchemas(entraId, signal) {
    const response = await axios.get(API_ENDPOINTS.tabularSchemas, {
      params: { entra_id: requireTabularIdentity(entraId) },
      signal,
    });
    return response.data;
  },

  async getTabularSchema(schemaId, entraId, signal) {
    const response = await axios.get(API_ENDPOINTS.tabularSchema(schemaId), {
      params: { entra_id: requireTabularIdentity(entraId) },
      signal,
    });
    return response.data;
  },

  async listExperimentDatasets(experimentId, entraId, signal) {
    const response = await axios.get(API_ENDPOINTS.experimentDatasets(experimentId), {
      params: { entra_id: requireTabularIdentity(entraId) },
      signal,
    });
    return response.data;
  },

  async previewDatasetImport(experimentId, body, entraId, signal) {
    const response = await axios.post(API_ENDPOINTS.datasetImportPreview(experimentId), {
      ...body,
      entra_id: requireTabularIdentity(entraId),
    }, {
      headers: { 'Content-Type': 'application/json' },
      signal,
    });
    return response.data;
  },

  // HTTP 202 acknowledges a queued import, not a completed dataset.
  async createDatasetImport(experimentId, body, entraId, signal) {
    const response = await axios.post(API_ENDPOINTS.experimentDatasets(experimentId), {
      ...body,
      entra_id: requireTabularIdentity(entraId),
    }, {
      headers: { 'Content-Type': 'application/json' },
      signal,
    });
    return response.data;
  },

  async getTabularDataset(datasetId, entraId, signal) {
    const response = await axios.get(API_ENDPOINTS.tabularDataset(datasetId), {
      params: { entra_id: requireTabularIdentity(entraId) },
      signal,
    });
    return response.data;
  },

  async cancelTabularJob(jobId, entraId, signal) {
    const response = await axios.post(API_ENDPOINTS.tabularJobCancel(jobId), {
      entra_id: requireTabularIdentity(entraId),
    }, { headers: { 'Content-Type': 'application/json' }, signal });
    return response.data;
  },

  async deleteTabularDataset(datasetId, entraId, signal) {
    const response = await axios.delete(API_ENDPOINTS.tabularDataset(datasetId), {
      params: { entra_id: requireTabularIdentity(entraId) }, signal,
    });
    // DatasetDeletionStatus fields were not specified. Use HTTP status and
    // refresh the documented dataset/job status endpoints, not guessed fields.
    return { status: response.status };
  },

  async createDatasetExport(datasetId, body, entraId, signal) {
    const response = await axios.post(API_ENDPOINTS.datasetExports(datasetId), {
      ...body, entra_id: requireTabularIdentity(entraId),
    }, { headers: { 'Content-Type': 'application/json' }, signal });
    return response.data;
  },

  datasetExportDownloadUrl(jobId, entraId) {
    return `${API_ENDPOINTS.tabularJobDownload(jobId)}?${new URLSearchParams({ entra_id: requireTabularIdentity(entraId) })}`;
  },

  async getTabularJob(jobId, entraId, signal) {
    const response = await axios.get(API_ENDPOINTS.tabularJob(jobId), {
      params: { entra_id: requireTabularIdentity(entraId) },
      signal,
    });
    return response.data;
  },

  async queryDatasetRows(datasetId, body, entraId, signal) {
    const response = await axios.post(API_ENDPOINTS.datasetRows(datasetId), {
      ...body,
      entra_id: requireTabularIdentity(entraId),
    }, {
      headers: { 'Content-Type': 'application/json' },
      signal,
    });
    return response.data;
  },

  async getProjectStructureTypes(entraId) {
    return this.get(API_ENDPOINTS.projectStructureTypes, {}, entraId);
  },

  async createProjectStructureType(data, entraId) {
    return this.createResource(API_ENDPOINTS.projectStructureTypes, data, entraId);
  },

  async updateProjectStructureType(typeId, data, entraId) {
    const response = await axios.patch(
      API_ENDPOINTS.projectStructureType(typeId),
      { ...data, entra_id: entraId },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data;
  },

  async getProjectStructureSets(entraId) {
    return this.get(API_ENDPOINTS.projectStructureSets, {}, entraId);
  },

  async getProjectStructureSet(setId, entraId) {
    return this.get(API_ENDPOINTS.projectStructureSet(setId), {}, entraId);
  },

  async createProjectStructureSet(data, entraId) {
    return this.createResource(API_ENDPOINTS.projectStructureSets, data, entraId);
  },

  async getProjectStructureSetRules(setId, entraId) {
    return this.get(API_ENDPOINTS.projectStructureSetRules(setId), {}, entraId);
  },

  async createProjectStructureSetRule(setId, data, entraId) {
    return this.createResource(API_ENDPOINTS.projectStructureSetRules(setId), data, entraId);
  },

  async updateProjectStructureSetRule(setId, ruleId, data, entraId) {
    const response = await axios.patch(
      API_ENDPOINTS.projectStructureSetRule(setId, ruleId),
      { ...data, entra_id: entraId },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data;
  },

  async deleteProjectStructureSetRule(setId, ruleId, entraId) {
    const response = await axios.delete(API_ENDPOINTS.projectStructureSetRule(setId, ruleId), {
      data: { entra_id: entraId },
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  },

  async createResource(endpoint, data, entraId) {
    try {
      const response = await axios.post(endpoint, { ...data, entra_id: entraId }, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to create resource at ${endpoint}:`, error);
      throw error;
    }
  },

  async createProjectTemplate(data) {
    try {
      const response = await axios.post(API_ENDPOINTS.projectTemplates, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      console.error('Failed to create project template:', error);
      throw error;
    }
  },

  async getProjectTemplates(entraId) {
    return this.get(API_ENDPOINTS.projectTemplates, {}, entraId);
  },

  async getProjectTemplate(templateId, entraId) {
    return this.get(API_ENDPOINTS.projectTemplate(templateId), {}, entraId);
  },

  async setInstrumentRentalPlace(instrument, rentalPlaceId, entraId) {
    const response = await axios.put(
      API_ENDPOINTS.instrument(instrument.id),
      { ...instrument, rental_place_id: rentalPlaceId, entra_id: entraId },
      { headers: { 'Content-Type': 'application/json' } }
    );
    return response.data;
  },

  async updateResource(endpoint, data, userId, entraId) {
    try {
      const requestData = { ...data, user_id: userId, entra_id: entraId };
      const response = await axios.put(endpoint, requestData, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to update resource at ${endpoint}:`, error);
      throw error;
    }
  },

  async deleteResource(endpoint, userId, entraId) {
    try {
      const response = await axios.delete(endpoint, {
        data: { user_id: userId, entra_id: entraId },
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to delete resource at ${endpoint}:`, error);
      throw error;
    }
  }
};
